import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Mail, Phone, ArrowRight } from 'lucide-react';
import { MarketingLayout } from '../../components/marketing/MarketingLayout';
import { useLanguage } from '../../contexts/LanguageContext';
import enCopy from './venuePartnerFaqCopy.en';
import elCopy from './venuePartnerFaqCopy.el';

// Ghost / partner-outreach FAQ page.
// Reached only via a direct URL shared in venue outreach emails
// (there is intentionally NO navigation link to this page from the
// marketing site header, footer or app). Uses the global language
// switcher in the marketing header for EN / EL.
//
// Copy lives in ./venuePartnerFaqCopy.{en,el}.js so that copy edits
// don't touch layout code, and testing agents can diff copy modules
// independently.
const CONTENT = { en: enCopy, el: elCopy };

const FONT_SERIF = "'Inria Serif', serif";
const FONT_SANS = "'Manrope', sans-serif";
const ACCENT = '#E53935';

export default function VenuePartnerFaqPage() {
  const { language } = useLanguage();
  const content = CONTENT[language] || CONTENT.en;
  const [openId, setOpenId] = useState(null);

  const allItems = useMemo(() => {
    // stable ids across language changes based on English category+index
    return content.categories.flatMap((group, gi) =>
      group.items.map((item, ii) => ({ ...item, id: `${gi}-${ii}`, category: group.title })),
    );
  }, [content]);
  // silence unused-var lint (kept for future search/deep-link support)
  void allItems;

  return (
    <MarketingLayout>
      <section
        data-testid="venue-partner-faq-hero"
        style={{ maxWidth: 920, margin: '0 auto', padding: '72px 22px 24px' }}
      >
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          data-testid="venue-partner-faq-eyebrow"
          style={{
            margin: 0,
            fontSize: 12,
            letterSpacing: 1.6,
            textTransform: 'uppercase',
            color: ACCENT,
            fontWeight: 700,
            fontFamily: FONT_SANS,
          }}
        >
          {content.eyebrow}
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, delay: 0.05 }}
          data-testid="venue-partner-faq-title"
          style={{
            margin: '14px 0 0',
            fontFamily: FONT_SERIF,
            fontSize: 'clamp(2.2rem, 4.6vw, 3.6rem)',
            lineHeight: 1.05,
            letterSpacing: '-0.02em',
            color: 'var(--mkt-text)',
          }}
        >
          {content.title}
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.15 }}
          data-testid="venue-partner-faq-intro"
          style={{
            margin: '22px 0 0',
            fontFamily: FONT_SANS,
            fontSize: 17,
            lineHeight: 1.65,
            color: 'var(--mkt-text-muted)',
            maxWidth: 720,
          }}
        >
          {content.intro}
        </motion.p>
      </section>

      {/* TL;DR */}
      <section style={{ maxWidth: 920, margin: '0 auto', padding: '20px 22px 12px' }}>
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.55 }}
          data-testid="venue-partner-faq-tldr"
          style={{
            background: 'linear-gradient(140deg, #161127 0%, #2A1B5A 100%)',
            color: '#FFFFFF',
            borderRadius: 24,
            padding: '30px 28px',
          }}
        >
          <p
            style={{
              margin: 0,
              fontSize: 11,
              letterSpacing: 1.6,
              textTransform: 'uppercase',
              color: ACCENT,
              fontWeight: 700,
              fontFamily: FONT_SANS,
            }}
          >
            {content.tldrEyebrow}
          </p>
          <p
            style={{
              margin: '14px 0 0',
              fontFamily: FONT_SANS,
              fontSize: 16,
              lineHeight: 1.7,
              color: 'rgba(255,255,255,0.86)',
            }}
          >
            {content.tldrBody}
          </p>
        </motion.div>
      </section>

      {/* Categorised Q&A */}
      <section
        data-testid="venue-partner-faq-body"
        style={{
          maxWidth: 920,
          margin: '0 auto',
          padding: '32px 22px 12px',
          display: 'flex',
          flexDirection: 'column',
          gap: 34,
        }}
      >
        {content.categories.map((group, gi) => (
          <div key={`${group.title}-${gi}`} data-testid={`venue-partner-faq-category-${gi}`}>
            <p
              style={{
                margin: '0 0 12px 4px',
                fontFamily: FONT_SANS,
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                color: 'var(--mkt-text-subtle)',
              }}
            >
              {group.title}
            </p>
            <div
              style={{
                background: 'var(--mkt-card-bg)',
                border: '1px solid var(--mkt-card-border)',
                borderRadius: 20,
                boxShadow: 'var(--mkt-card-shadow)',
                overflow: 'hidden',
              }}
            >
              {group.items.map((item, ii) => {
                const id = `${gi}-${ii}`;
                const isOpen = openId === id;
                return (
                  <div
                    key={id}
                    data-testid={`venue-partner-faq-row-${id}`}
                    style={{
                      borderTop: ii === 0 ? 'none' : '1px solid var(--mkt-card-border)',
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => setOpenId((current) => (current === id ? null : id))}
                      data-testid={`venue-partner-faq-item-${id}`}
                      aria-expanded={isOpen}
                      style={{
                        width: '100%',
                        background: 'none',
                        border: 'none',
                        textAlign: 'left',
                        padding: '18px 22px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 14,
                        cursor: 'pointer',
                      }}
                    >
                      <span
                        data-testid={`venue-partner-faq-question-${id}`}
                        style={{
                          fontFamily: FONT_SANS,
                          fontSize: 16,
                          fontWeight: 700,
                          color: 'var(--mkt-text)',
                          lineHeight: 1.35,
                        }}
                      >
                        {item.q}
                      </span>
                      <ChevronDown
                        size={18}
                        color={ACCENT}
                        style={{
                          transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 200ms ease',
                          flexShrink: 0,
                        }}
                      />
                    </button>
                    <AnimatePresence initial={false}>
                      {isOpen ? (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.24 }}
                          style={{ overflow: 'hidden' }}
                        >
                          <p
                            data-testid={`venue-partner-faq-answer-${id}`}
                            style={{
                              margin: 0,
                              padding: '0 22px 20px',
                              fontFamily: FONT_SANS,
                              fontSize: 15,
                              color: 'var(--mkt-text-muted)',
                              lineHeight: 1.7,
                            }}
                          >
                            {item.a}
                          </p>
                        </motion.div>
                      ) : null}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </section>

      {/* Contact block */}
      <section
        data-testid="venue-partner-faq-contact"
        style={{ maxWidth: 920, margin: '0 auto', padding: '48px 22px 100px' }}
      >
        <div
          style={{
            background: 'var(--mkt-card-bg-alt)',
            border: '1px solid var(--mkt-card-border)',
            borderRadius: 26,
            padding: 'clamp(28px, 4vw, 44px)',
          }}
        >
          <p
            style={{
              margin: 0,
              fontSize: 12,
              letterSpacing: 1.6,
              textTransform: 'uppercase',
              color: ACCENT,
              fontWeight: 700,
              fontFamily: FONT_SANS,
            }}
          >
            {content.contactEyebrow}
          </p>
          <h2
            style={{
              margin: '10px 0 12px',
              fontFamily: FONT_SERIF,
              fontSize: 'clamp(1.6rem, 3vw, 2.2rem)',
              letterSpacing: '-0.01em',
              color: 'var(--mkt-text)',
              lineHeight: 1.15,
            }}
          >
            {content.contactTitle}
          </h2>
          <p
            style={{
              margin: '0 0 26px',
              fontFamily: FONT_SANS,
              fontSize: 15,
              lineHeight: 1.65,
              color: 'var(--mkt-text-muted)',
              maxWidth: 620,
            }}
          >
            {content.contactBody}
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <a
              href={`mailto:${content.ctaEmail}`}
              data-testid="venue-partner-faq-email"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 12,
                textDecoration: 'none',
                color: 'var(--mkt-text)',
                fontFamily: FONT_SANS,
                fontSize: 16,
                fontWeight: 700,
              }}
            >
              <span
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 12,
                  background: 'var(--mkt-accent-red-surface)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Mail size={16} color={ACCENT} />
              </span>
              {content.ctaEmail}
            </a>
            <a
              href={`tel:${content.ctaPhone1.replace(/\s+/g, '')}`}
              data-testid="venue-partner-faq-phone-1"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 12,
                textDecoration: 'none',
                color: 'var(--mkt-text)',
                fontFamily: FONT_SANS,
                fontSize: 16,
                fontWeight: 700,
              }}
            >
              <span
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 12,
                  background: 'var(--mkt-accent-red-surface)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Phone size={16} color={ACCENT} />
              </span>
              {content.ctaPhone1}
            </a>
            <a
              href={`tel:${content.ctaPhone2.replace(/\s+/g, '')}`}
              data-testid="venue-partner-faq-phone-2"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 12,
                textDecoration: 'none',
                color: 'var(--mkt-text)',
                fontFamily: FONT_SANS,
                fontSize: 16,
                fontWeight: 700,
              }}
            >
              <span
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 12,
                  background: 'var(--mkt-accent-red-surface)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Phone size={16} color={ACCENT} />
              </span>
              {content.ctaPhone2}
            </a>
          </div>

          <p
            style={{
              margin: '22px 0 0',
              fontFamily: FONT_SANS,
              fontSize: 13,
              color: 'var(--mkt-text-subtle)',
            }}
          >
            {content.replyPromise}
          </p>

          <a
            href="/welcome"
            data-testid="venue-partner-faq-browse-home"
            style={{
              marginTop: 26,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '12px 20px',
              borderRadius: 9999,
              background: 'var(--mkt-cta-solid-bg)',
              color: 'var(--mkt-cta-solid-text)',
              textDecoration: 'none',
              fontFamily: FONT_SANS,
              fontSize: 14,
              fontWeight: 700,
              boxShadow: '0 10px 22px rgba(22,17,39,0.18)',
            }}
          >
            {content.ctaBrowseHome} <ArrowRight size={14} />
          </a>
        </div>
      </section>
    </MarketingLayout>
  );
}
